import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Grid,
    Typography,
    Avatar,
    Button,
    Chip,
    IconButton,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Switch,
    FormControlLabel,
    Paper,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Language as WebsiteIcon,
    DeleteForever as DeleteForeverIcon,
    PhotoCamera as CameraIcon,
    Verified as VerifiedIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Edit as EditIcon,
    LinkedIn as LinkedInIcon,
    GitHub as GitHubIcon,
    Twitter as TwitterIcon,
    Security as SecurityIcon,
} from '@mui/icons-material';
import authApi from "Service/Auth.api";
import { setAlertData } from "../../Redux/Screen/Action";
import { clearUser } from "../../Redux/User/Action";
import DeleteProfileModal from "Components/Dashboard/DeleteProfileModal";
import ColorData from 'Components/CstmTheme/ColorData';
import ProfileCard from './components/Common/ProfileCard';
import { UserProfile } from './types/profile.types';

// Mock data - replace with actual data from your API/Redux store
const mockUserData: UserProfile = {
    id: '1',
    personalInfo: {
        firstName: 'Rohit',
        lastName: 'Deshmukh',
        dateOfBirth: '1990-05-15',
        gender: 'male',
        profilePicture: '', // Empty for demo - will show initials
    },
    professionalInfo: {
        jobTitle: 'Senior Software Engineer',
        company: 'Tech Solutions Inc.',
        experience: 5,
        specializations: ['React', 'Node.js', 'TypeScript', 'AWS', 'MongoDB'],
        certifications: [
            {
                id: '1',
                name: 'AWS Certified Developer',
                issuer: 'Amazon Web Services',
                dateIssued: '2023-01-15',
                expiryDate: '2026-01-15',
            },
            {
                id: '2',
                name: 'React Professional Certificate',
                issuer: 'Meta',
                dateIssued: '2022-08-20',
            },
        ],
    },
    contactInfo: {
        email: 'rohit.deshmukh@example.com',
        phone: '+91 9876543210',
        alternativeContact: 'rohit.work@example.com',
        socialMedia: {
            linkedin: 'https://linkedin.com/in/rohitdeshmukh',
            github: 'https://github.com/rohitdeshmukh',
            twitter: 'https://twitter.com/rohitdeshmukh',
            website: 'https://rohitdeshmukh.dev',
        },
    },
    preferences: {
        notifications: {
            email: true,
            sms: false,
            push: true,
            marketing: false,
        },
        privacy: {
            profileVisibility: 'public',
            showEmail: true,
            showPhone: false,
        },
        language: 'English',
        theme: 'light',
    },
    security: {
        lastLogin: '2024-01-15T10:30:00Z',
        passwordLastChanged: '2023-12-01T08:00:00Z',
        twoFactorEnabled: true,
        activeSessions: [
            {
                id: '1',
                device: 'Chrome on Windows',
                location: 'Mumbai, India',
                lastActive: '2024-01-15T10:30:00Z',
                current: true,
            },
        ],
    },
    metadata: {
        createdAt: '2022-01-01T00:00:00Z',
        updatedAt: '2024-01-15T10:30:00Z',
        isVerified: true,
    },
};

const Profile: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [userData] = useState<UserProfile>(mockUserData);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const dispatch = useDispatch();

    const handleDeleteConfirm = async (payload: any) => {
        try {
            const res: any = await authApi.deleteProfile(payload);
            if (res.success) {
                localStorage.clear();
                dispatch(clearUser() as any);
                window.location.href = "/home.html";
            } else {
                dispatch(
                    setAlertData({
                        alertMessage: res?.alert?.alertMessage || "Failed to delete profile.",
                        openAlert: true,
                        alertVariant: "error",
                    })
                );
            }
        } catch (error) {
            console.error("Error deleting profile:", error);
            dispatch(
                setAlertData({
                    alertMessage: "An unexpected error occurred while deleting the profile.",
                    openAlert: true,
                    alertVariant: "error",
                })
            );
        } finally {
            setDeleteModalOpen(false);
        }
    };

    // Get user initials for avatar
    const getUserInitials = (firstName: string, lastName: string) => {
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    // Format date
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    // Profile Header Component
    const ProfileHeader = () => (
        <ProfileCard title="Profile Overview">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'center' : 'flex-start',
                    gap: 3,
                }}
            >
                {/* Profile Picture */}
                <Box sx={{ position: 'relative', textAlign: 'center' }}>
                    <Avatar
                        src={userData.personalInfo.profilePicture}
                        sx={{
                            width: isMobile ? 100 : 120,
                            height: isMobile ? 100 : 120,
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            bgcolor: ColorData.primary.main,
                            color: ColorData.text.secondary,
                            border: `4px solid ${ColorData.secondary.main}`,
                        }}
                    >
                        {getUserInitials(userData.personalInfo.firstName, userData.personalInfo.lastName)}
                    </Avatar>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            bottom: -5,
                            right: -5,
                            bgcolor: ColorData.primary.main,
                            color: ColorData.text.secondary,
                            '&:hover': {
                                bgcolor: ColorData.hover.main,
                            },
                            width: 35,
                            height: 35,
                        }}
                    >
                        <CameraIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Basic Info */}
                <Box sx={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: ColorData.heading.main }}>
                            {userData.personalInfo.firstName} {userData.personalInfo.lastName}
                        </Typography>
                        {userData.metadata.isVerified && (
                            <VerifiedIcon sx={{ color: ColorData.primary.main, fontSize: '1.5rem' }} />
                        )}
                    </Box>

                    <Typography variant="h6" sx={{ color: ColorData.text.primary, mb: 2 }}>
                        {userData.professionalInfo.jobTitle} at {userData.professionalInfo.company}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, justifyContent: isMobile ? 'center' : 'flex-start' }}>
                        <Chip
                            icon={<EmailIcon />}
                            label={userData.contactInfo.email}
                            variant="outlined"
                            sx={{ borderColor: ColorData.primary.main }}
                        />
                        {userData.contactInfo.phone && (
                            <Chip
                                icon={<PhoneIcon />}
                                label={userData.contactInfo.phone}
                                variant="outlined"
                                sx={{ borderColor: ColorData.primary.main }}
                            />
                        )}
                    </Box>

                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        sx={{
                            bgcolor: ColorData.primary.main,
                            '&:hover': {
                                bgcolor: ColorData.hover.main,
                            },
                        }}
                    >
                        Edit Profile
                    </Button>
                </Box>

                {/* Quick Stats */}
                <Box sx={{ textAlign: 'center' }}>
                    <Paper
                        sx={{
                            p: 2,
                            bgcolor: ColorData.secondary.main,
                            borderRadius: 2,
                            minWidth: 150,
                        }}
                    >
                        <Typography variant="body2" color="textSecondary">
                            Member Since
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: ColorData.primary.main }}>
                            {formatDate(userData.metadata.createdAt)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            Experience
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: ColorData.primary.main }}>
                            {userData.professionalInfo.experience} Years
                        </Typography>
                    </Paper>
                </Box>
            </Box>
        </ProfileCard>
    );

    // Professional Info Section
    const ProfessionalInfoSection = () => (
        <ProfileCard
            title="Professional Information"
            actions={
                <IconButton>
                    <EditIcon />
                </IconButton>
            }
        >
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                            Job Title
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {userData.professionalInfo.jobTitle}
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                            Company
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {userData.professionalInfo.company}
                        </Typography>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                            Specializations
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {userData.professionalInfo.specializations.map((skill, index) => (
                                <Chip
                                    key={index}
                                    label={skill}
                                    sx={{
                                        bgcolor: ColorData.primary.main,
                                        color: ColorData.text.secondary,
                                        '&:hover': {
                                            bgcolor: ColorData.hover.main,
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Certifications
                    </Typography>
                    {userData.professionalInfo.certifications.map((cert) => (
                        <Box
                            key={cert.id}
                            sx={{
                                p: 2,
                                mb: 1,
                                bgcolor: ColorData.secondary.main,
                                borderRadius: 1,
                                border: `1px solid ${ColorData.stroke.main}`,
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {cert.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {cert.issuer} • Issued {formatDate(cert.dateIssued)}
                                {cert.expiryDate && ` • Expires ${formatDate(cert.expiryDate)}`}
                            </Typography>
                        </Box>
                    ))}
                </Grid>
            </Grid>
        </ProfileCard>
    );

    // Contact Information Section
    const ContactInfoSection = () => (
        <ProfileCard
            title="Contact Information"
            actions={
                <IconButton>
                    <EditIcon />
                </IconButton>
            }
        >
            <List>
                <ListItem>
                    <ListItemIcon>
                        <EmailIcon sx={{ color: ColorData.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Email"
                        secondary={userData.contactInfo.email}
                    />
                </ListItem>
                {userData.contactInfo.phone && (
                    <ListItem>
                        <ListItemIcon>
                            <PhoneIcon sx={{ color: ColorData.primary.main }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="Phone"
                            secondary={userData.contactInfo.phone}
                        />
                    </ListItem>
                )}
                {userData.contactInfo.alternativeContact && (
                    <ListItem>
                        <ListItemIcon>
                            <EmailIcon sx={{ color: ColorData.primary.main }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="Alternative Email"
                            secondary={userData.contactInfo.alternativeContact}
                        />
                    </ListItem>
                )}
            </List>

            {userData.contactInfo.socialMedia && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                        Social Media
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {userData.contactInfo.socialMedia.linkedin && (
                            <IconButton
                                component="a"
                                href={userData.contactInfo.socialMedia.linkedin}
                                target="_blank"
                                sx={{ color: '#0077B5' }}
                            >
                                <LinkedInIcon />
                            </IconButton>
                        )}
                        {userData.contactInfo.socialMedia.github && (
                            <IconButton
                                component="a"
                                href={userData.contactInfo.socialMedia.github}
                                target="_blank"
                                sx={{ color: '#333' }}
                            >
                                <GitHubIcon />
                            </IconButton>
                        )}
                        {userData.contactInfo.socialMedia.twitter && (
                            <IconButton
                                component="a"
                                href={userData.contactInfo.socialMedia.twitter}
                                target="_blank"
                                sx={{ color: '#1DA1F2' }}
                            >
                                <TwitterIcon />
                            </IconButton>
                        )}
                        {userData.contactInfo.socialMedia.website && (
                            <IconButton
                                component="a"
                                href={userData.contactInfo.socialMedia.website}
                                target="_blank"
                                sx={{ color: ColorData.primary.main }}
                            >
                                <WebsiteIcon />
                            </IconButton>
                        )}
                    </Box>
                </>
            )}
        </ProfileCard>
    );

    // Preferences Section
    const PreferencesSection = () => (
        <ProfileCard title="Preferences & Settings">
            <Box>
                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
                    Notifications
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={userData.preferences.notifications.email}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: ColorData.primary.main,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: ColorData.primary.main,
                                },
                            }}
                        />
                    }
                    label="Email Notifications"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={userData.preferences.notifications.push}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: ColorData.primary.main,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: ColorData.primary.main,
                                },
                            }}
                        />
                    }
                    label="Push Notifications"
                />

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
                    Privacy
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={userData.preferences.privacy.showEmail}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: ColorData.primary.main,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: ColorData.primary.main,
                                },
                            }}
                        />
                    }
                    label="Show Email Publicly"
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={userData.preferences.privacy.showPhone}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: ColorData.primary.main,
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: ColorData.primary.main,
                                },
                            }}
                        />
                    }
                    label="Show Phone Publicly"
                />
            </Box>
        </ProfileCard>
    );

    // Security Section
    const SecuritySection = () => (
        <ProfileCard title="Security & Account">
            <List>
                <ListItem>
                    <ListItemIcon>
                        <SecurityIcon sx={{ color: ColorData.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Last Login"
                        secondary={formatDate(userData.security.lastLogin!)}
                    />
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        <SecurityIcon sx={{ color: ColorData.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Password Last Changed"
                        secondary={formatDate(userData.security.passwordLastChanged!)}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        sx={{
                            borderColor: ColorData.primary.main,
                            color: ColorData.primary.main,
                            '&:hover': {
                                borderColor: ColorData.hover.main,
                                color: ColorData.hover.main,
                            },
                        }}
                    >
                        Change
                    </Button>
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        <SecurityIcon sx={{ color: ColorData.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                        primary="Two-Factor Authentication"
                        secondary={userData.security.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                    />
                    <Chip
                        label={userData.security.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                        color={userData.security.twoFactorEnabled ? 'success' : 'warning'}
                        size="small"
                    />
                </ListItem>
            </List>
        </ProfileCard>
    );

    // Danger Zone Section
    const DangerZoneSection = () => (
        <ProfileCard title="Danger Zone">
            <Box sx={{ p: 1 }}>
                <Typography variant="body2" color="text.primary" sx={{ mb: 2 }}>
                    Once you delete your account, there is no going back. Please be certain.
                </Typography>
                <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteForeverIcon />}
                    onClick={() => setDeleteModalOpen(true)}
                    sx={{
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            bgcolor: '#d32f2f',
                        },
                    }}
                >
                    Delete Account
                </Button>
            </Box>
        </ProfileCard>
    );

    return (
        <Box sx={{ p: 3, bgcolor: '#f9f0e7', minHeight: '100vh' }}>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 'bold',
                    color: ColorData.heading.main,
                    mb: 3,
                    textAlign: isMobile ? 'center' : 'left',
                }}
            >
                My Profile
            </Typography>

            <Grid container spacing={3}>
                {/* Profile Header - Full Width */}
                <Grid size={{ xs: 12 }}>
                    <ProfileHeader />
                </Grid>

                {/* Professional Info */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <ProfessionalInfoSection />
                </Grid>

                {/* Contact Info */}
                <Grid size={{ xs: 12, md: 4 }}>
                    <ContactInfoSection />
                </Grid>

                {/* Preferences */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <PreferencesSection />
                </Grid>

                {/* Security */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <SecuritySection />
                </Grid>

                {/* Danger Zone */}
                <Grid size={{ xs: 12 }}>
                    <DangerZoneSection />
                </Grid>
            </Grid>

            <DeleteProfileModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
            />
        </Box>
    );
};

export default Profile;

